<template>
  <div class="lRegister">
    <LightVariation :button="headerButton" />
    <OfferTimerComponent
      v-if="offer?.expiresAt"
      color="danger"
      @finishOffer="finishOffer"
      :expire-at="offer.expiresAt"
    />
    <main class="lRegister__main">
      <section class="lRegister__main-steps">
        <CheckoutLightStep label="Identificação" :is-active="true" />
        <div class="lRegister__main-steps--divider" />
        <CheckoutLightStep label="Pagamento" :is-active="false" />
      </section>
      <fieldset>
        <FormLegend title="Crie sua conta para iniciar sua assinatura" />
        <BaseForm
          :schema="schema"
          :submit="submit"
          submit-button-label="Criar conta"
          has-floating-label
          has-icon-message
        />
      </fieldset>
    </main>
  </div>
</template>

<script setup>
import { computed, onMounted, ref } from 'vue';
import { useStore } from 'vuex';
import { useRoute } from 'vue-router';
import BaseForm from '@/components/BaseForm';
import { useToast } from 'vue-toastification';
import form from '@/Utils/defaultFormSettings.js';
import useNavigate from '@/composables/useNavigate';
import FormLegend from '@/components/form/FormLegend';
import useDatalayer from '@/composables/useDatalayer';
import { utils as utilsMixins } from '@/mixins/utils';
import useErrorMessages from '@/composables/useErrorMessages';
import LightVariation from '@/components/header/LightVariation';
import CheckoutLightStep from '@/components/checkout/CheckoutLightStep';
import useEcommerceCheckout from '@/composables/useEcommerceCheckout';
import OfferTimerComponent from '@/components/offerTimer/OfferTimerComponent/OfferTimerComponent';

const { offer } = useEcommerceCheckout();
const store = useStore();
const toast = useToast();
const route = useRoute();
const navigate = useNavigate();
const datalayer = useDatalayer();
const errorMessage = useErrorMessages();

const headerButton = { route: { name: 'Login' }, label: 'Entrar' };
const PROCEED_TO_SUBSCRIPTION_ERROR = 'PROCEED_TO_SUBSCRIPTION';
const ERRORS_REDIRECT_TO_LOGIN = ['SUBSCRIPTION_IS_ON_TRIAL', 'SUBSCRIPTION_ALREADY_ACTIVE'];

const userID = ref('');
const retryUpsertCart = ref(0);
const offerSlug = ref(navigate.getRouteParam('slug') || process.env.VUE_APP_DEFAULT_OFFER);

const filledUserEmail = computed(() => navigate.getQueryParam('userEmail') || '');
const decryptEmail = computed(() => {
  const encodedEmail = navigate.getQueryParam('e') || '';
  if (!encodedEmail) return '';

  try {
    return atob(encodedEmail);
  } catch (e) {
    return '';
  }
});
const schema = computed(() => [
  { ...form.FULLNAME, placeholder: '', colorMode: 'light' },
  { ...form.PHONE, placeholder: '', colorMode: 'light', autocomplete: 'off' },
  { ...form.EMAIL, placeholder: '', colorMode: 'light', value: decryptEmail.value || filledUserEmail.value },
  { ...form.PASSWORD, placeholder: '', colorMode: 'light' },
  { ...form.LGPD, colorMode: 'light' },
]);

const goToPaymentStep = async (isNewAccount = false) => {
  if (!isNewAccount) {
    await navigate.removeQueryParam('cf');
  }

  navigate.to('ShortCheckoutPayment', { userID: userID.value, slug: offerSlug.value });
};

const shouldRedirectToPayment = (response) => {
  return response?.data?.proceedToSubscription;
};

const handleErrorResponse = (fullError, email) => {
  const { error, data } = fullError;

  const hasAccess = !!data?.user?.userID;

  if (hasAccess && shouldRedirectToPayment(fullError)) {
    userID.value = data?.user?.userID;

    toast.success(errorMessage.getValue(PROCEED_TO_SUBSCRIPTION_ERROR));

    goToPaymentStep();
  } else if (hasAccess && !shouldRedirectToPayment(fullError)) {
    toast.success('Identificamos sua conta. Você será redirecionado à página de Login!');
    setTimeout(() => {
      navigate.to('Login', { email }, { offerSlug: route.params.slug });
    }, 5000);
  } else {
    toast.error(errorMessage.getValue(error));
  }
};

const finishOffer = () => {
  navigate.to(
    'ShortCheckout',
    {
      slug: process.env.VUE_APP_DEFAULT_OFFER,
    },
    {
      ...route.query,
    }
  );
};

const submit = async (formData) => {
  try {
    const [firstname, ...complementName] = formData.name.trim().split(' ');
    const url = window.location.href;

    const hasFBC = utilsMixins.methods.getCookie('_fbc');
    const hasFBP = utilsMixins.methods.getCookie('_fbp');
    const convertedPhoneNumber = formData.phone.trim()?.replace(/\D/gm, '');
    if (convertedPhoneNumber.length !== 10 && convertedPhoneNumber.length !== 11) {
      return;
    }
    const registerData = {
      firstname,
      lastname: complementName.join(' '),
      phone: formData.phone,
      email: formData.email.toLowerCase(),
      password: formData.password,
      meta: {
        marketingConsent: formData.lgpd,
        url: utilsMixins.methods.hasPMP(url) ? url : utilsMixins.methods.addDefaultPMP(url),
        fbc: hasFBC,
        fbp: hasFBP,
      },
    };

    const [error, data] = await store.dispatch('subscription/userRegister', { ...registerData });

    store.dispatch('subscription/setRegisterFilledFields', {
      name: formData.name,
      email: formData.email,
      phone: formData.phone,
    });

    if (error) {
      handleErrorResponse(error, registerData.email);
    } else {
      toast.success('Cadastro criado com sucesso!');
      userID.value = data?.user?.userID;
      datalayer.createLightCheckoutEvent(2);
      goToPaymentStep(true);
    }
  } catch (error) {
    console.log('## GPRIM ## error', error);
    toast.error(errorMessage.getValue());
  }
};

const handleOfferRead = async () => {
  let response = await store.dispatch('offer/offerRead', { offerSlug: offerSlug.value });

  if (response?.error === 'OFFER_NOT_FOUND' && !retryUpsertCart.value) {
    retryUpsertCart.value += 1;
    offerSlug.value = process.env.VUE_APP_DEFAULT_OFFER;
    response = await store.dispatch('offer/offerRead', { offerSlug: offerSlug.value });
    if (!response) {
      finishOffer();
    }
    if (response?.error === 'OFFER_NOT_FOUND' && retryUpsertCart.value >= 1) {
      navigate.to('NotFound');
    }
  }
};

onMounted(() => {
  handleOfferRead();
  navigate.addQueryParams({ cf: 1 });
  datalayer.createLightCheckoutEvent(1);
});
</script>

<style lang="scss" scoped>
.lRegister {
  height: 100vh;
  display: flex;
  flex-direction: column;
  align-items: stretch;
  background-color: $white;

  &__main {
    flex: 1;
    padding: 16px;
    background: url('@/assets/img/checkout/light_mobile_background.png') no-repeat right center;
    background-color: $white;

    &-steps {
      width: 100%;
      display: flex;
      justify-content: space-between;
      align-items: flex-start;
      margin: 32px 0 32px;

      &--divider {
        flex: 1;
        border: 0.5px dashed $dark-60;
      }
    }

    > fieldset {
      > legend {
        padding-left: 0.25rem;
        margin-bottom: 32px;
      }

      > form :deep(.grp-button-main) {
        border: none;
        background-color: $seer-stone;
        color: $white;

        &:disabled {
          opacity: 0.4;
        }
      }
    }
  }
}

@media (min-width: 768px) {
  .lRegister {
    &__main {
      display: flex;
      flex-direction: column;
      justify-content: center;
      align-items: center;
      background: url('@/assets/img/checkout/light_tablet_background.png') no-repeat right center;
      background-color: $white;

      > section,
      fieldset {
        width: 65%;

        > legend {
          max-width: unset;
        }
      }
    }
  }
}

@media (min-width: 1440px) {
  .lRegister {
    &__main {
      justify-content: flex-start;
      padding-top: 40px;
      background: url('@/assets/img/checkout/light_desktop_background.png') no-repeat right center;
      background-color: $white;

      > section {
        width: 176px;
      }

      > fieldset {
        width: 384px;

        > legend {
          max-width: 80%;
          font-family: 'Proxima Nova Bold', Verdana, Tahoma, sans-serif;
          text-align: center;
        }
      }
    }
  }
}
</style>
